import { useMutation }                                                            from '@apollo/client'
import { ArrowUturnLeftIcon }                                                     from '@heroicons/react/24/solid'
import { useState }                                                               from 'react'
import { useDispatch, useSelector }                                               from 'react-redux'
import { useNavigate }                                                            from 'react-router-dom'
import { setSizeGuidesRefetch }                                                   from '../../../features/shop/shopSlice'
import { setToasts, showToaster }                                                 from '../../../features/toaster/toasterSlice'
import { CREATE_SHOP_SIZE_GUIDE, DELETE_SIZE_GUIDE_FILE, UPDATE_SHOP_SIZE_GUIDE } from '../../../graphql/shop'
import { uploadFile }                                                             from '../../../utilities'
import { Button, TextField }                                                      from '../../index'
import { LoadingEllipsis }                                                        from '../../Loaders'

export default function SettingsShopSizeGuideForm( props: any ): JSX.Element {
  const { sizeGuide, refetch }              = props
  const dispatch: any                       = useDispatch()
  const navigate: any                       = useNavigate()
  const currentTenant: any                  = useSelector( ( state: any ): any => state.currentTenant.tenant )
  const [ sizeGuideName, setSizeGuideName ] = useState( sizeGuide?.name ?? '' )
  const [ sizeGuideUrl ]                   = useState( sizeGuide?.url ?? '' )
  const [ nameError, setNameError ]         = useState( false )
  const [ fileToUpload, setFileToUpload ]   = useState( null )

  const [ deleteSizeGuideFile ] = useMutation( DELETE_SIZE_GUIDE_FILE, {
    onError: ( e: any ): any => console.error( 'old image not deleted: ', e )
  } )

  const resetErrors: any = (): void => {
    setNameError( false )
  }

  const checkErrors: any = (): boolean => {
    resetErrors()

    let hasErrors: boolean = false

    if ( !sizeGuideName.length || sizeGuideName.length < 3 ) {
      setNameError( true )
      hasErrors = true
    }

    return hasErrors
  }

  const handleFileChange: any = async ( e: any ): Promise<void> => {
    if ( e.target.files ) {
      const file: any = e.target.files[ 0 ]

      if ( file.size > 10485760 ) {
        alert( 'Max file size is 10MB. File NOT uploaded!' )
      } else {
        setFileToUpload( file )
      }
    }
  }

  const handleSubmit: any = async (): Promise<any> => {
    if ( checkErrors() || creating || updating ) return

    const formData: any = {
      name: sizeGuideName,
      url: sizeGuideUrl
    }

    if ( fileToUpload ) {
      const newFileUrl: string = await uploadFile( fileToUpload, ( currentTenant.uuid + '/size_guides' ), 'true' )

      if ( newFileUrl ) {
        formData.url = newFileUrl

        if ( sizeGuideUrl.length > 0 ) {
          await deleteSizeGuideFile( { variables: { input: { url: sizeGuideUrl } } } )
        }
      }
    }

    if ( !formData.url || formData.url.length < 6 ) {
      dispatchMessage( 'error', 'Size guide image is required' )
      return
    }

    formData.file_ext = formData.url.split( '.' ).pop()

    if ( sizeGuide ) {
      formData.id = sizeGuide.id
      await updateSizeGuide( { variables: { input: formData } } )
    } else {
      await createSizeGuide( { variables: { input: formData } } )
    }

    await dispatch( setSizeGuidesRefetch( true ) )
  }

  const [ createSizeGuide, { loading: creating } ] = useMutation( CREATE_SHOP_SIZE_GUIDE, {
    onCompleted: (): void => {
      navigate( '/settings/shop/size-guides' )
      dispatchMessage( 'success', 'Size guide created!' )
    },
    onError:     (): void => dispatchMessage( 'error', 'Creating failed. Please try again' )
  } )

  const [ updateSizeGuide, { loading: updating } ] = useMutation( UPDATE_SHOP_SIZE_GUIDE, {
    onCompleted: (): void => {
      refetch()
      dispatchMessage( 'success', 'Size guide updated!' )
    },
    onError:     (): void => dispatchMessage( 'error', 'Update failed. Please try again' )
  } )

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  return (
    <>
      <div className="w-24 my-4">
        <Button
          label="Back"
          blue
          onClick={ (): any => navigate( -1 ) }
          icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
        />
      </div>
      <div className="flex flex-col w-full bg-white rounded-3xl p-4 xl:p-5 space-y-3">
        { ( creating || updating ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

        { !creating && !updating &&
          <>
            <div className="flex flex-wrap flex-col w-full mb-6">
              { sizeGuideUrl &&
                <a href={ `${ process.env.REACT_APP_CDN }/${ sizeGuideUrl }` } target="_blank" className="mb-4">
                  <span>Click on the image for preview</span>
                  <img className="w-48" src={ `${ process.env.REACT_APP_CDN }/${ sizeGuideUrl }` } alt={ sizeGuideName } />
                </a>
              }

              <div className="flex flex-col mb-4">
                <div>{ sizeGuide ? 'Select new image to upload (old image will be deleted)' : 'Select image to upload' }</div>
                <input id="file" type="file" accept="image/*" onChange={ handleFileChange } />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="text-blue-header w-full mb-3">Name</div>
              <TextField
                label=""
                value={ sizeGuideName }
                name="name"
                type="text"
                onChange={ setSizeGuideName }
                placeholder="Name"
                wrapperClasses="w-full"
                fieldClasses={ `py-4 px-5 border ${ nameError ? 'border-red-600' : 'border-grey-light' } rounded-2xl w-full text-blue-input font-medium placeholder-grey-input` }
                isRequired
              />
            </div>

            <div className="flex flex-wrap w-full">
              <div className="flex pt-5">
                <Button label={ sizeGuide ? 'Update size guide' : 'Add new size guide' } yellow onClick={ handleSubmit } />
              </div>
            </div>
          </>
        }
      </div>
    </>
  )
}
